import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Key, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { apiKeysApi, knowledgeBaseApi, suppressionsApi, webhooksApi } from '@/api/misc';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Field } from '@/components/ui/field';
import { Loader } from '@/components/ui/loader';
import { Topbar } from '@/components/ui/topbar';
import { ApiError } from '@/lib/api-client';
import { qk } from '@/lib/query-keys';

/**
 * Settings — combines team, api keys, suppressions, webhooks, knowledge
 * base, and the AI voice/prompt views into a single tabbed page. Fine
 * for the port; split into per-tab routes later if the tabs grow.
 */
type Tab = 'api-keys' | 'suppressions' | 'webhooks' | 'knowledge-base';

const TABS: { id: Tab; label: string }[] = [
  { id: 'api-keys', label: 'API keys' },
  { id: 'suppressions', label: 'Suppressions' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'knowledge-base', label: 'Knowledge base' },
];

export function SettingsPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('api-keys');

  return (
    <>
      <Topbar title="Settings" />
      <div className="flex-1 overflow-auto">
        <div className="border-b border-mb-border bg-mb-surface-elev px-6 flex gap-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'px-3 py-3 text-sm border-b-2 transition-colors ' +
                (tab === t.id
                  ? 'border-mb-accent text-mb-text font-medium'
                  : 'border-transparent text-mb-text-muted hover:text-mb-text')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-6 max-w-3xl">
          {tab === 'api-keys' && <ApiKeysPanel />}
          {tab === 'suppressions' && <SuppressionsPanel />}
          {tab === 'webhooks' && <WebhooksPanel />}
          {tab === 'knowledge-base' && <KnowledgeBasePanel />}
        </div>
      </div>
    </>
  );
}

function ApiKeysPanel(): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: qk.apiKeys.all, queryFn: () => apiKeysApi.list() });
  const [justCreated, setJustCreated] = useState<{ name: string; key: string } | null>(null);
  const [name, setName] = useState('');
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => apiKeysApi.create({ name: name.trim() }),
    onSuccess: async (row) => {
      setJustCreated({ name: row.name, key: row.key });
      setName('');
      await qc.invalidateQueries({ queryKey: qk.apiKeys.all });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys.all }),
  });

  const keys = list.data ?? [];
  const active = keys.filter((k) => !k.revoked_at);

  return (
    <div className="space-y-4">
      <div className="mb-card p-5 space-y-3">
        <h3 className="font-display font-semibold text-mb-text">Create a new key</h3>
        <Field label="Name" hint="e.g. n8n production">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mb-input"
            maxLength={80}
          />
        </Field>
        <div className="flex justify-end">
          <button
            type="button"
            className="mb-btn-primary"
            disabled={create.isPending || !name.trim()}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create
          </button>
        </div>
      </div>

      {justCreated ? (
        <div className="mb-card p-5 border-l-4 border-mb-success space-y-3">
          <div className="font-display font-semibold text-mb-text">
            Copy this key now — it won&rsquo;t be shown again
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded-md bg-mb-surface border border-mb-border text-sm font-mono break-all">
              {justCreated.key}
            </code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(justCreated.key);
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
              }}
              className="mb-btn-primary"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            type="button"
            className="text-xs text-mb-text-muted hover:text-mb-text"
            onClick={() => setJustCreated(null)}
          >
            Done — close
          </button>
        </div>
      ) : null}

      <div>
        <h3 className="font-display font-semibold text-mb-text text-sm mb-2">
          Active keys ({active.length})
        </h3>
        {list.isLoading ? (
          <Loader />
        ) : list.isError ? (
          <ErrorState onRetry={() => void list.refetch()} />
        ) : active.length === 0 ? (
          <EmptyState icon={Key} title="No active keys" />
        ) : (
          active.map((k) => (
            <div key={k.id} className="mb-card p-4 flex items-center gap-3 mb-2">
              <Key className="h-4 w-4 text-mb-accent-press" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-mb-text truncate">{k.name}</div>
                <div className="text-xs text-mb-text-subtle">
                  <code className="font-mono">{k.key_prefix}…</code> · created{' '}
                  {new Date(k.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Revoke "${k.name}"?`)) revoke.mutate(k.id);
                }}
                className="text-xs text-mb-text-muted hover:text-mb-danger inline-flex items-center gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SuppressionsPanel(): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: qk.suppressions.all,
    queryFn: () => suppressionsApi.list(),
  });
  const [email, setEmail] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => suppressionsApi.add({ email: email.trim(), reason: 'manual' }),
    onSuccess: async () => {
      setEmail('');
      await qc.invalidateQueries({ queryKey: qk.suppressions.all });
    },
    onError: (e) =>
      setSubmitError(e instanceof ApiError ? e.message : 'Failed to add'),
  });
  const del = useMutation({
    mutationFn: (id: string) => suppressionsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.suppressions.all }),
  });

  const items = list.data ?? [];

  return (
    <div className="space-y-4">
      <div className="mb-card p-5 space-y-3">
        <h3 className="font-display font-semibold text-mb-text">Add to suppression list</h3>
        <div className="flex gap-2">
          <input
            type="email"
            className="mb-input flex-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="somebody@example.com"
          />
          <button
            type="button"
            className="mb-btn-primary"
            disabled={add.isPending || !email}
            onClick={() => {
              setSubmitError(null);
              add.mutate();
            }}
          >
            {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Add
          </button>
        </div>
        {submitError ? (
          <div role="alert" className="text-xs text-mb-danger">{submitError}</div>
        ) : null}
      </div>
      {list.isLoading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="text-sm text-mb-text-muted">No suppressed emails.</div>
      ) : (
        <div className="space-y-1">
          {items.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-sm border-b border-mb-border py-2">
              <div>
                <span className="font-mono">{s.email}</span>
                <span className="text-mb-text-muted ml-2">· {s.reason}</span>
              </div>
              <button
                type="button"
                onClick={() => del.mutate(s.id)}
                className="text-xs text-mb-text-muted hover:text-mb-danger"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WebhooksPanel(): JSX.Element {
  const list = useQuery({
    queryKey: qk.webhooks.all,
    queryFn: () => webhooksApi.overview(),
  });
  if (list.isLoading) return <Loader />;
  if (list.isError) return <ErrorState onRetry={() => void list.refetch()} />;
  const endpoints = list.data?.items ?? [];
  return (
    <div className="space-y-3">
      <div className="text-sm text-mb-text-muted">
        {endpoints.length} configured endpoint{endpoints.length === 1 ? '' : 's'}.
        Full endpoint editor lands post-port.
      </div>
      {endpoints.map((e) => (
        <div key={e.id} className="mb-card p-3 text-sm">
          <div className="font-mono truncate">{e.url}</div>
          <div className="text-xs text-mb-text-muted mt-1">
            {e.event_types.length ? e.event_types.join(', ') : '(all events)'}
          </div>
        </div>
      ))}
    </div>
  );
}

function KnowledgeBasePanel(): JSX.Element {
  const list = useQuery({
    queryKey: qk.knowledgeBase.all,
    queryFn: () => knowledgeBaseApi.list(),
  });
  if (list.isLoading) return <Loader />;
  if (list.isError) return <ErrorState onRetry={() => void list.refetch()} />;
  const docs = list.data ?? [];
  return (
    <div className="space-y-3">
      <div className="text-sm text-mb-text-muted">
        {docs.length} document{docs.length === 1 ? '' : 's'}.
        Full uploader lands post-port; extracted text is what Markdown Marty consumes.
      </div>
      {docs.map((d) => (
        <div key={d.id} className="mb-card p-3 text-sm">
          <div className="font-medium">{d.name}</div>
          <div className="text-xs text-mb-text-muted mt-1">
            {d.kind} · {d.text_length.toLocaleString()} chars
          </div>
        </div>
      ))}
    </div>
  );
}
